import type { FandomCmp } from "./Consent";
import type { FandomContext } from "./FandomContext";
import type { FandomTrack } from "./Tracking";
import type {
  FandomMediaWikiGlobal,
  FeaturedVideoConfig,
} from "./MediaWikiGlobal";

export type * from "./Consent";
export * from "./Tracking";
export type * from "./FandomContext";
export type * from "./QueryParameters";
export type * from "./MediaWikiGlobal";

declare global {
  interface Window {
    /**
     * The tracking object. Provides an events queue that are later on handled by the tracking module
     * in a race condition safe way.
     * @see {@link FandomTrack.q}
     */
    fandomTrack?: FandomTrack;
    /**
     * Consent information provided from the CMP stubs and identity engine.
     *
     * For notifications when consent has updated, add a listener to the window object for
     * `fandomConsentUpdate`.
     *
     * ```ts
     * window.addEventListener("fandomConsentUpdate", (fandomCmp: FandomCmp) => {
     *   // window.fandomCmp is also guranteed to be available at this point as well
     * })
     * ```
     *
     * @see {@link FandomCmp} for type information
     */
    fandomCmp?: FandomCmp;
    /**
     * @deprecated Use `window.addEventListener("fandomConsentUpdate")` to get updates when
     * consent has changed and use {@link fandomCmp window.fandomCmp} to get access to the
     * current consent data.
     */
    fandomCmpEvents?: CustomEvent[];

    ads?: FandomLegacyAds;
    fandomContext?: FandomContext;
    /**
     * Set by UCP#ArticleVideoController
     * It references `window.fandomIsVideoPossible` which checks:
     *  - params
     *  - allowed countries
     *  - maxed out player impressions
     */
    canPlayVideo?: () => boolean;
    /**
     * MediaWiki configuration object.
     * @see {@link https://www.mediawiki.org/wiki/ResourceLoader/Core_modules#mediaWiki.config}
     */
    mw?: FandomMediaWikiGlobal;
    /**
     * ResourceLoader configuration object.
     * @see {@link https://github.com/Wikia/mediawiki/blob/master/includes/ResourceLoader/ClientHtml.php#L301}
     */
    RLCONF?: ResourceLoaderConfig;
  }
}

export enum APP {
  FANDOM_DESKTOP = "fandomdesktop",
  FANDOM_MOBILE = "fandommobile",
}

export type FandomLegacyContextTargeting = {
  pageType?: string;
  hasFeaturedVideo?: boolean;
  wikiVertical?: string;
};

export type FandomLegacyContext = {
  platform: string;
  pageType?: string;
  targeting?: FandomLegacyContextTargeting;
  opts: {
    isAdTestWiki?: boolean;
    /**
     * The user email hashes are sourced from the MediaWiki platform
     * @see {@link https://github.com/Wikia/unified-platform/blob/master/extensions/fandom/FandomIncludes/src/User/UserInfo.php#L74-L76}
     * */
    userEmailHashes?: string[];
    /**
     * When a wiki is a Gamepedia wiki, this will be present and true, otherwise
     * it will be undefined.
     */
    isGamepedia?: boolean;
  };
};

export type FandomLegacyAds = {
  context: FandomLegacyContext;
  runtime?: {
    video?: {
      adUnit?: string;
    };
  };
};

export type ResourceLoaderConfig = {
  wgCityId: number;
  wgUserId: number;
  wgArticleFeaturedVideo?: FeaturedVideoConfig;
};
